import React, { useEffect, useState } from 'react';
import { ShoppingCart, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import unknownLogo from '../assets/unknown_logo.png';

interface DiscountInfo {
  originalPrice: number;
  discountedPrice: number;
  discountPercentage: number;
  discountEndDate: string;
}

interface UnpaidReportCardProps {
  id: number;
  company_name: string;
  company_logo_url?: string;
  price: number;
  discountInfo?: DiscountInfo;
  currencySymbol: string;
  onClick: (reportId: number, reportPrice: number, discountInfo?: DiscountInfo) => void;
  summaryDownloadUrls: Record<string, string>;
}

const UnpaidReportCard: React.FC<UnpaidReportCardProps> = ({
  id,
  company_name,
  company_logo_url,
  price,
  discountInfo,
  currencySymbol,
  onClick,
  summaryDownloadUrls,
}) => {
  const { t, i18n } = useTranslation();
  const [remainingTime, setRemainingTime] = useState('');
  const [downloadError, setDownloadError] = useState<string>('');

  // Idioma seleccionado: el de i18n, fallback 'en'
  const currentLang = i18n.language?.split('-')[0] || 'en';
  const summaryLang = summaryDownloadUrls[currentLang]
    ? currentLang
    : summaryDownloadUrls['en']
    ? 'en'
    : null;

  // Cálculo del tiempo restante del descuento
  useEffect(() => {
    if (!discountInfo?.discountEndDate) return;
    const interval = setInterval(() => {
      const end = new Date(discountInfo.discountEndDate);
      const now = new Date();
      const diff = end.getTime() - now.getTime();

      if (diff > 0) {
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        setRemainingTime(`${days}d ${hours}h ${minutes}m ${seconds}s`);
      } else {
        setRemainingTime(t('discountExpired') || 'Descuento finalizado');
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [discountInfo, t]);

  const handleBuyClick = () => {
    onClick(id, price, discountInfo);
  };

  const handlePreviewClick = () => {
    if (!summaryLang) {
      setDownloadError(t('reportDetails.no_download_available'));
      setTimeout(() => setDownloadError(''), 4000);
      return;
    }
    const url = summaryDownloadUrls[summaryLang];
    if (!url) {
      setDownloadError(t('reportDetails.no_download_available'));
      setTimeout(() => setDownloadError(''), 4000);
      return;
    }
    if (url === 'test') {
      setDownloadError(t('reportDetails.test_download_error'));
      setTimeout(() => setDownloadError(''), 4000);
      return;
    }
    window.open(url, '_blank');
  };

  return (
    <div className="bg-white border-2 border-blue-100 rounded-xl hover:shadow-lg transition-shadow relative flex flex-col h-full overflow-hidden">
      {/* Badge de descuento */}
      {discountInfo && (
        <div className="absolute top-4 right-4 bg-red-500 text-white text-xl font-bold px-3 py-1 rounded-full shadow z-10">
          -{discountInfo.discountPercentage}%
        </div>
      )}

      {/* Cabecera con logo y resumen */}
      <div className="p-6 flex flex-col items-center">
        <img
          src={company_logo_url || unknownLogo}
          alt={company_name}
          className="w-28 h-28 rounded-lg object-cover"
        />
        <h3 className="font-bold text-2xl mt-4 text-center">{company_name}</h3>

        {summaryLang ? (
  <button
    onClick={handlePreviewClick}
    title={t('unpaidReportCard.viewSummary', 'Ver resumen')}
    className="
      group mt-2 inline-flex items-center gap-2 rounded-full
      border border-slate-200 bg-gradient-to-b from-white to-slate-50
      px-3.5 py-1.5 text-sm font-medium text-slate-700
      shadow-sm hover:shadow-md hover:border-slate-300
      focus:outline-none focus:ring-2 focus:ring-blue-500/40
      transition
    "
  >
    <span
      className="
        inline-flex h-5 w-5 items-center justify-center rounded-full
        bg-blue-50 group-hover:bg-blue-100 transition
      "
    >
      <FileText className="h-3.5 w-3.5 text-blue-600" />
    </span>
    {t('unpaidReportCard.viewSummary', 'Ver resumen')}
    <span
      className="
        ml-1 inline-flex items-center justify-center rounded
        bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600
      "
    >
      {summaryLang.toUpperCase()}
    </span>
    <svg
      className="ml-1 h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5"
      viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"
    >
      <path d="M7 5l5 5-5 5" />
    </svg>
  </button>
) : (
  <span
    className="
      mt-2 inline-flex items-center gap-2 rounded-full
      border border-slate-200 bg-slate-50 px-3 py-1.5
      text-sm text-slate-400
    "
  >
    <FileText className="h-4 w-4" />
    {t('unpaidReportCard.noSummary', 'Sin resumen')}
  </span>
)}
      </div>

      {/* Cuerpo */}
      <div className="p-6 pt-0 flex-1 flex flex-col items-center justify-center text-center">
        <p className="text-gray-600 text-base mb-4">
          {t('unpaidReportCard.description')}
        </p>

        {discountInfo ? (
          <div className="flex items-baseline space-x-3">
            <span className="text-xl text-gray-400 line-through">
              {discountInfo.originalPrice}{currencySymbol}
            </span>
            <span className="text-4xl font-extrabold text-blue-700">
              {discountInfo.discountedPrice}{currencySymbol}
            </span>
          </div>
        ) : (
          <span className="text-4xl font-bold text-blue-700">
            {price}{currencySymbol}
          </span>
        )}

        {downloadError && (
          <div className="mt-3 text-red-600 text-sm font-semibold">{downloadError}</div>
        )}
      </div>

      {/* Pie */}
      <div className="px-6 pb-6">
        <button
          onClick={handleBuyClick}
          className="w-full inline-flex items-center justify-center rounded-md bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-4 text-lg font-semibold text-white hover:from-blue-700 hover:to-blue-800 transition"
        >
          <ShoppingCart className="mr-2 h-6 w-6" />
          {t('unpaidReportCard.buyNow')}
        </button>

        {discountInfo && remainingTime && (
          <p className="mt-3 text-red-600 text-sm font-medium text-center">
            {remainingTime}
          </p>
        )}
      </div>
    </div>
  );
};

export default UnpaidReportCard;
